/**
 * Patient Safety Assessment - WHO PSA
 * WHO assessment instrument with scored components (0-3 value per item)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import assessmentInstrumentService from '../../services/assessmentInstrumentService';
import hospitalCapabilityProfileService from '../../services/hospitalCapabilityProfileService';
import { useNotifications } from '../NotificationSystem';
import './WHOAssessment.css';

const INSTRUMENT_ID = 'WHO_PSA_2008';

const PatientSafetyAssessment = ({ organizationId, userId, userName, onComplete }) => {
  const { t, i18n } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [activeComponentIndex, setActiveComponentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const useRo = (i18n.language || 'ro').startsWith('ro');

  useEffect(() => {
    let cancelled = false;
    assessmentInstrumentService.getInstrument(INSTRUMENT_ID).then((data) => {
      if (!cancelled) {
        setInstrument(data);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        console.error('PSA load:', err);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!organizationId || !userId) {
      notify.error(t('qms:selectOrganization') || 'Select an organization.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await hospitalCapabilityProfileService.submitScoredAssessment(
        organizationId,
        answers,
        userId,
        userName,
        INSTRUMENT_ID
      );
      notify.success(t('qms:whoAssessments.assessmentSubmitted') || 'Assessment submitted successfully.');
      
      // Calculate component breakdown for results view
      const components = instrument?.components || [];
      const componentStats = components.map((comp) => {
        let compScore = 0;
        let compMaxScore = 0;
        for (const q of comp.questions || []) {
          const ans = answers[q.id];
          const opt = (q.options || []).find((o) => o.value === ans);
          compScore += opt?.value ?? 0;
          compMaxScore += Math.max(...(q.options || []).map((o) => o.value || 0), 0);
        }
        return {
          name: comp.name,
          nameRo: comp.nameRo,
          score: compScore,
          maxScore: compMaxScore
        };
      });
      
      // Calculate total score and maturity level
      let totalScore = 0;
      let maxScore = 0;
      for (const comp of components) {
        for (const q of comp.questions || []) {
          const ans = answers[q.id];
          const opt = (q.options || []).find((o) => o.value === ans);
          totalScore += opt?.value ?? 0;
          maxScore += Math.max(...(q.options || []).map((o) => o.value || 0), 0);
        }
      }
      
      let maturityLevel = null;
      if (instrument?.maturityLevels) {
        for (const level of instrument.maturityLevels) {
          if (totalScore >= level.minScore && totalScore <= level.maxScore) {
            maturityLevel = level;
            break;
          }
        }
      }
      
      // Pass full result including instrument, components breakdown, score, and maturity level
      onComplete?.({
        ...result,
        instrument,
        components: componentStats,
        score: totalScore,
        maxScore,
        maturityLevel,
        answers
      });
    } catch (err) {
      notify.error(err.message || t('common:saveFailed') || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="who-assessment loading">
        <p>{t('qms:loading') || 'Loading…'}</p>
      </div>
    );
  }

  if (!instrument) {
    return (
      <div className="who-assessment error">
        <p>{t('qms:whoAssessments.instrumentNotFound') || 'Assessment instrument not found. Please contact your administrator.'}</p>
      </div>
    );
  }

  const components = instrument.components || [];
  const activeComp = components[activeComponentIndex];
  const totalQuestions = components.reduce((s, c) => s + (c.questions?.length || 0), 0);
  const answeredCount = Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== null && answers[k] !== '').length;
  const canSubmit = answeredCount >= totalQuestions;

  let currentScore = 0;
  let maxPossibleScore = 0;
  for (const comp of components) {
    for (const q of comp.questions || []) {
      const ans = answers[q.id];
      const opt = (q.options || []).find((o) => o.value === ans);
      currentScore += opt?.value ?? 0;
      maxPossibleScore += Math.max(...(q.options || []).map((o) => o.value || 0), 0);
    }
  }

  let maturityLevel = null;
  if (instrument.maturityLevels) {
    const avgScore = totalQuestions > 0 ? currentScore / answeredCount : 0;
    for (const level of instrument.maturityLevels) {
      if (avgScore >= level.minScore && avgScore <= level.maxScore) {
        maturityLevel = level;
        break;
      }
    }
  }

  return (
    <div className="who-assessment">
      <div className="who-assessment-header-row">
        <div className="who-assessment-header-titles">
          <h2>{useRo ? (instrument.nameRo || instrument.name) : (instrument.name || instrument.nameRo)}</h2>
          {(instrument.descriptionRo || instrument.description) && (
            <p className="who-assessment-header-subtitle">
              {useRo ? (instrument.descriptionRo || instrument.description) : (instrument.description || instrument.descriptionRo)}
            </p>
          )}
        </div>
        <div className="who-assessment-header-meta">
          <span className="who-assessment-pill" aria-label={t('qms:whoAssessments.questionsAnswered')}>
            {answeredCount} / {totalQuestions} {useRo ? 'răspunse' : 'answered'}
          </span>
          {maturityLevel && (
            <span className="maturity-badge">{useRo ? maturityLevel.labelRo : maturityLevel.label}</span>
          )}
          <span className="who-assessment-pill" aria-label={t('qms:whoAssessments.currentScore')}>
            {currentScore} / {maxPossibleScore}
          </span>
          <div className="who-assessment-submit-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
            >
              {submitting ? (t('qms:saving') || 'Saving…') : (t('qms:whoAssessments.submitAssessment') || 'Submit Assessment')}
            </button>
          </div>
        </div>
      </div>

      <div className="who-assessment-with-sidebar">
        <nav className="who-assessment-sidebar" aria-label={t('qms:whoAssessments.componentBreakdown') || 'Components'}>
          <div className="who-assessment-sidebar-nav">
            {components.map((comp, idx) => {
              const compQuestions = comp.questions || [];
              const compAnswered = compQuestions.filter((q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '').length;
              const isCompleted = compQuestions.length > 0 && compAnswered === compQuestions.length;
              return (
                <button
                  key={comp.id}
                  type="button"
                  className={`who-assessment-sidebar-item ${idx === activeComponentIndex ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                  onClick={() => setActiveComponentIndex(idx)}
                >
                  <span className="who-assessment-sidebar-item-name">
                    {comp.code}. {useRo ? (comp.nameRo || comp.nameEn) : (comp.nameEn || comp.nameRo)}
                  </span>
                  <span className="who-assessment-sidebar-item-count">
                    {compAnswered} / {compQuestions.length}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <main className="who-assessment-main">
          {activeComp && (
            <>
              <div className="who-assessment-chapter-divider">
                <span className="who-assessment-chapter-badge">{activeComp.code}</span>
                <span className="who-assessment-chapter-line" />
              </div>
              {(activeComp.questions || []).map((q) => (
                <div key={q.id} className="who-assessment-question-card">
                  <div className="who-assessment-question-inner">
                    <span className="who-assessment-question-code">{q.code}</span>
                    <p className="who-assessment-question-text">
                      {useRo ? (q.textRo || q.textEn) : (q.textEn || q.textRo)}
                    </p>
                  </div>
                  <div className="q-options">
                    {(q.options || []).map((opt) => (
                      <label key={opt.value} className="q-option">
                        <input
                          type="radio"
                          name={q.id}
                          value={opt.value}
                          checked={answers[q.id] === opt.value}
                          onChange={() => handleAnswer(q.id, opt.value)}
                        />
                        <span>{useRo ? (opt.labelRo || opt.label) : (opt.label || opt.labelRo)}</span>
                        {opt.value !== undefined && <span className="option-score">({opt.value})</span>}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default PatientSafetyAssessment;
